
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, Settings, PlusCircle, User as UserIcon } from "lucide-react";
import { Avatar } from "@/components/User/Avatar";
import { useAuth } from "@/context/AuthContext";

export interface NavbarUserMenuProps {
  handleSignInClick: () => void;
  handleSignOutClick: () => void;
}

export function NavbarUserMenu({ handleSignInClick, handleSignOutClick }: NavbarUserMenuProps) {
  const { user, userProfile, loading } = useAuth();

  // Show loading state during auth check
  if (loading) {
    return (
      <div className="ml-2 w-8 h-8 rounded-full bg-muted animate-pulse" />
    );
  }

  if (!user || !userProfile) {
    return (
      <Button variant="default" size="sm" className="ml-2" onClick={handleSignInClick}>
        <LogIn className="h-4 w-4 mr-2" />
        Sign In
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="ml-2 p-0 h-8 w-8 rounded-full">
          <Avatar userId={user.id} avatarId={userProfile.avatarId} size="sm" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userProfile.username}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {userProfile.subscriptionTier ? `${userProfile.subscriptionTier} tier` : "Free account"}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/profile" className="cursor-pointer">
              <UserIcon className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/create-prompt" className="cursor-pointer">
              <PlusCircle className="mr-2 h-4 w-4" />
              <span>Create Prompt</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings" className="cursor-pointer">
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOutClick} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
